import * as Sentry from '@sentry/browser'
import { get } from 'lodash'
import moment from 'moment'
import ApiSwitch from 'src/api/util/apiSwitch'
import { getSafeSessionStorage } from 'src/helpers/browser'
import { flashNotification } from 'src/actions'
import { fetchValuationHistory } from 'src/actions/valuationHistory'
import {
  fetchHomeInsurance,
  setHomeInsuranceOverride,
} from 'src/actions/homeInsurance'
import {
  ERRORS,
  NO_ACCESS_ERROR,
  HOME_INSURANCE_CTA,
} from 'src/constants/messages'

import {
  createLoan,
  deleteLoan,
  submitLoanBalance,
  trackClientHomeEvent,
  updateLoan,
} from 'src/api/mikasa/requests'

import { getAuthEmail } from 'src/auth'
import {
  selectHomeId,
  selectHomeData,
  getHomeUserFromEmail,
} from 'src/store/selectors/home'
import { selectClientFetchId, selectClientId } from 'src/store/selectors/client'
import { selectAuthedAsClient } from 'src/store/selectors/auth'
import { selectLatestValuationForCurrentHome } from 'src/store/selectors/valuationHistory'

import {
  SET_HOME_FETCH_ID,
  SET_HOME_FROM_CACHE,
  SET_CLIENT_FETCH_ID,
  FETCH_HOME_REQUEST,
  FETCH_HOME_SUCCESS,
  FETCH_HOME_FAILURE,
  UPDATE_HOME_REQUEST,
  UPDATE_HOME_SUCCESS,
  UPDATE_HOME_ERROR,
  ADD_HOME_ANSWER_REQUEST,
  ADD_HOME_ANSWER_SUCCESS,
  ADD_HOME_ANSWER_ERROR,
  CREATE_HOME_LOAN_REQUEST,
  CREATE_HOME_LOAN_SUCCESS,
  CREATE_HOME_LOAN_FAILURE,
  DELETE_LOAN_REQUEST,
  DELETE_LOAN_SUCCESS,
  DELETE_LOAN_FAILURE,
  UPDATE_LOAN_REQUEST,
  UPDATE_LOAN_SUCCESS,
  UPDATE_LOAN_FAILURE,
  UPDATE_LOAN_BALANCES_REQUEST,
  UPDATE_LOAN_BALANCES_SUCCESS,
  UPDATE_LOAN_BALANCES_FAILURE,
  UPDATE_LOAN_BALANCES_SAVED_RESET,
  UNSET_HOME_INSURANCE,
} from './actionTypes'

export const fetchHomeDetail = homeId => async (dispatch, getState) => {
  const state = getState()
  const { cache } = state.home
  const isCached = Boolean(cache[homeId])
  const clientFetchId = selectClientFetchId(state)

  dispatch({
    type: FETCH_HOME_REQUEST,
    id: homeId,
    loading: !isCached,
  })

  if (clientFetchId !== null) {
    dispatch(fetchValuationHistory(clientFetchId, homeId))
  }

  try {
    const { response } = await ApiSwitch.fetch({
      action: 'getHomeDetail',
      service: 'mikasa',
      homeId,
    })

    dispatch({
      type: FETCH_HOME_SUCCESS,
      data: response.data,
    })

    // Home data to set clientId for old home routes
    if (clientFetchId === null) {
      dispatch({
        type: SET_CLIENT_FETCH_ID,
        id: getHomeUserFromEmail(response.data, getAuthEmail()).uuid,
      })
    }
  } catch (error) {
    dispatch({
      type: FETCH_HOME_FAILURE,
      error,
    })
  }
}

export const setCurrentHomeDetail = homeId => async (dispatch, getState) => {
  const { cache } = getState().home
  const isCached = Boolean(cache[homeId])

  dispatch({
    type: homeId && isCached ? SET_HOME_FROM_CACHE : SET_HOME_FETCH_ID,
    id: homeId,
    keepCache: true,
  })

  dispatch({
    type: UNSET_HOME_INSURANCE,
  })
}

export const updateHomeInsurancePolicy =
  ({ doNavBack, homeUuid, attrs } = {}) =>
  async dispatch => {
    const onSuccess = () => {
      dispatch(fetchHomeInsurance(homeUuid))
      dispatch(
        flashNotification({
          message: HOME_INSURANCE_CTA.confirmDetailsSuccess,
          messageType: 'success',
        })
      )
      dispatch(setHomeInsuranceOverride({ override: true }))
      doNavBack()
    }
    const onFailure = () =>
      flashNotification({
        message: ERRORS.defaultHeadline,
        messageType: 'error',
      })

    await dispatch(
      updateHome(attrs, {
        homeUuid,
        service: 'mikasa',
        onSuccess,
        onFailure,
      })
    )
  }

export const updateHome =
  (data, { service = 'node', homeUuid, onSuccess, onFailure } = {}) =>
  async (dispatch, getState) => {
    dispatch({
      type: UPDATE_HOME_REQUEST,
    })

    const id = homeUuid || getState().home.data.id || selectHomeId(getState())

    try {
      let response

      // TODO: use api adapter
      if (service === 'mikasa') {
        const requestModule = await import('src/api/mikasa/requests.ts')
        response = await requestModule.updateHome(id, data)
      } else {
        const requestModule = await import('src/api/node/requests.js')
        response = await requestModule.updateHome(id, data)
      }

      dispatch({
        type: UPDATE_HOME_SUCCESS,
        data: response.data,
      })

      if (typeof onSuccess === 'function') onSuccess()
    } catch (error) {
      if (typeof failure === 'function') onFailure()

      dispatch({
        type: UPDATE_HOME_ERROR,
        error,
      })
    }
  }

export const handleAcknowledgeClientHasSeenAvmDiff =
  () => async (dispatch, getState) => {
    const state = getState()
    const latestValuationForCurrentHome =
      selectLatestValuationForCurrentHome(state)

    dispatch(
      addHomeAnswer({
        question: 'dateOfLastClientAcknowledgedValuation',
        answer: latestValuationForCurrentHome.date,
      })
    )
  }

export const addSnoozeAnswer = () => async dispatch => {
  const onSuccess = () =>
    dispatch(
      flashNotification({
        message: HOME_INSURANCE_CTA.snoozeSuccess,
        messageType: 'success',
      })
    )

  const onFailure = () =>
    dispatch(
      flashNotification({
        message: [ERRORS.defaultHeadline, ERRORS.defaultMessage],
        messageType: 'error',
      })
    )

  await dispatch(
    addHomeAnswer(
      {
        question: 'snoozeHomeInsuranceReminder',
        answer: 'true',
      },
      true,
      { onSuccess, onFailure }
    )
  )
}

export const addHomeAnswer =
  (answerData, withHomeUpdate = true, { onSuccess, onFailure } = {}) =>
  async (dispatch, getState) => {
    dispatch({
      type: ADD_HOME_ANSWER_REQUEST,
    })

    // TODO: remove id when we fully deprecate node
    const { id, uuid: homeUuid } = getState().home.data

    try {
      const { response } = await ApiSwitch.fetch({
        action: 'addHomeAnswer',
        service: 'mikasa',
        id,
        answerData,
        homeUuid,
      })

      dispatch({
        type: ADD_HOME_ANSWER_SUCCESS,
        data: response.data,
      })

      if (typeof onSuccess === 'function') onSuccess()

      if (withHomeUpdate) {
        await dispatch(fetchHomeDetail(id))
      }
    } catch (error) {
      if (typeof onFailure === 'function') onFailure()

      dispatch({
        type: ADD_HOME_ANSWER_ERROR,
        error,
      })
    }
  }

export const createHomeLoan =
  (homeUuid, data) => async (dispatch, getState) => {
    dispatch({
      type: CREATE_HOME_LOAN_REQUEST,
    })

    try {
      const response = await createLoan(homeUuid, data)
      dispatch({
        type: CREATE_HOME_LOAN_SUCCESS,
        data: response.data,
      })

      const state = getState()
      dispatch(fetchHomeDetail(selectHomeId(state)))
    } catch (error) {
      dispatch({
        type: CREATE_HOME_LOAN_FAILURE,
        error,
      })
    }
  }

export const updateLoanBalances =
  loanAmountMap => async (dispatch, getState) => {
    dispatch({
      type: UPDATE_LOAN_BALANCES_REQUEST,
    })
    try {
      const now = moment().toJSON()
      const promises = Object.keys(loanAmountMap).map(key =>
        submitLoanBalance(key, loanAmountMap[key], now)
      )

      await Promise.all(promises).then(responses => {
        dispatch({
          type: UPDATE_LOAN_BALANCES_SUCCESS,
          data: responses,
        })
      })

      const state = getState()
      dispatch(fetchHomeDetail(selectHomeId(state)))
    } catch (error) {
      dispatch({
        type: UPDATE_LOAN_BALANCES_FAILURE,
        error,
      })
      if (error.message.includes('403')) {
        dispatch(
          flashNotification({
            message: NO_ACCESS_ERROR.defaultMessage,
            messageType: 'error',
          })
        )
      } else {
        dispatch(
          flashNotification({
            message: [ERRORS.defaultHeadline, ERRORS.defaultMessage],
            messageType: 'error',
          })
        )
      }
    }
  }

export const resetLoanBalancedSaved = () => dispatch => {
  dispatch({ type: UPDATE_LOAN_BALANCES_SAVED_RESET })
}

export const deleteHomeLoan = data => async (dispatch, getState) => {
  const { loanId: loanUuid, homeId } = getState().loan.currentLoan

  dispatch({
    type: DELETE_LOAN_REQUEST,
  })

  try {
    const response = await deleteLoan(loanUuid)
    dispatch({
      type: DELETE_LOAN_SUCCESS,
      data: response.data,
    })

    dispatch(fetchHomeDetail(homeId))
  } catch (error) {
    dispatch({
      type: DELETE_LOAN_FAILURE,
      error,
    })
  }
}

export const updateHomeLoan = data => async dispatch => {
  const { loanUuid, homeId, ...loanProperties } = data

  dispatch({
    type: UPDATE_LOAN_REQUEST,
  })

  try {
    const response = await updateLoan(loanUuid, loanProperties)
    dispatch({
      type: UPDATE_LOAN_SUCCESS,
      data: response.data,
    })

    dispatch(fetchHomeDetail(homeId))
  } catch (error) {
    dispatch({
      type: UPDATE_LOAN_FAILURE,
      error,
    })
  }
}

export const trackHomeActivityMikasa = params => async (dispatch, getState) => {
  const state = getState()
  const home = selectHomeData(state)
  const clientId = selectClientId(state)

  const homeId = get(home, 'uuid')

  const eventName = get(params, 'eventName')
  const eventProperties = get(params, 'eventProperties')

  const authedAsCustomer = !selectAuthedAsClient(state)

  if (!clientId || !homeId || !eventName || authedAsCustomer) {
    return
  }

  try {
    await trackClientHomeEvent(eventName, clientId, homeId, eventProperties)
  } catch (e) {
    Sentry.captureException(e, {
      extra: {
        clientId,
        homeId,
        eventName,
        eventProperties,
      },
    })
  }
}

export const trackRefiSliderInteraction = () => dispatch => {
  dispatch(
    trackHomeActivityMikasa({
      eventName: 'refi-slider-interaction',
      eventProperties: {
        activity_source: 'user',
      },
    })
  )
}

export const trackInstantOfferRequested = () => dispatch => {
  dispatch(
    trackHomeActivityMikasa({
      eventName: 'instant-offer-requested',
      eventProperties: {
        activity_source: 'user',
      },
    })
  )
}

export const trackReverseMortgageView = () => dispatch => {
  dispatch(
    trackHomeActivityMikasa({
      eventName: 'viewed-reverse-mortgage-panel',
      eventProperties: {
        activity_source: 'user',
      },
    })
  )
}

export const trackCMARequest = () => dispatch =>
  dispatch(
    trackHomeActivityMikasa({
      eventName: 'cma-request',
      eventProperties: {
        activity_source: 'user',
      },
    })
  )

const sessionStorage = getSafeSessionStorage()

const setHomeTrackingEventSubmitted = key => sessionStorage.setItem(key, true)

export const getHomeTrackingEventSubmitted = key => sessionStorage.getItem(key)

export const trackHomeDigestViewEvent = eventName => dispatch => {
  if (!getHomeTrackingEventSubmitted(eventName)) {
    dispatch(
      trackHomeActivityMikasa({
        eventName,
        eventProperties: {
          activity_source: 'user',
        },
      })
    )
    setHomeTrackingEventSubmitted(eventName)
  }
}
